// @react-compiler-disable
import {
  CreateJobsiteMutationInput,
  Dtos_V2_JobSite_JobSiteDto,
  UpdateJobSiteMutationInput
} from '@/common/infrastructure/gql/generated/schema';
import { JobSiteFormData, JobSiteTableData } from '../types/JobSiteTypes';

type JobSite = {
  jobsiteId: string;
  jobName?: string | null;
  location?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  jobPhone?: string | null;
  flags?: { powerline?: boolean | null } | null;
  customer?: { id?: string | null; name?: string | null } | null;
  deletable?: boolean | null;
  jobsiteType?: string | null;
};

export const mapJobSiteToTableData = (jobSite: JobSite): JobSiteTableData => {
  return {
    id: parseInt(jobSite.jobsiteId, 10) || 0,
    customer: jobSite.customer?.name ?? 'N/A',
    jobSiteName: jobSite.jobName ?? 'N/A',
    jobSiteType: jobSite.jobsiteType ?? undefined,
    address: jobSite.location ?? 'N/A',
    latitude: jobSite.latitude?.toString() ?? 'N/A',
    longitude: jobSite.longitude?.toString() ?? 'N/A',
    jobPhone: jobSite.jobPhone?.trim() || undefined,
    powerlinePresent: jobSite.flags?.powerline ?? undefined,
    deletable: jobSite.deletable ?? false
  };
};

export const mapJobSiteToOption = (jobSite: JobSiteTableData) => {
  const address = jobSite.address && jobSite.address !== 'N/A' ? ` - ${jobSite.address}` : '';
  return {
    id: jobSite.id,
    value: jobSite.id,
    label: `${jobSite.jobSiteName}${address}`
  };
};

export const mapFormDataToCreateInput = (formData: JobSiteFormData): CreateJobsiteMutationInput => {
  if (!formData.customerId || !formData.address || formData.powerlinePresent === undefined) {
    throw new Error('Required fields are missing for creating a job site');
  }
  if (!formData.city || !formData.state) {
    throw new Error('City or State data are missing for creating a job site');
  }

  return {
    customerId: formData.customerId,
    address: formData.address.trim(),
    city: formData.city.trim(),
    state: formData.state.trim(),
    latitude: Number(formData.latitude),
    longitude: Number(formData.longitude),
    powerlinePresent: formData.powerlinePresent,
    ...(formData.jobSiteTypeId && { jobSiteType: { id: formData.jobSiteTypeId } }),
    ...(formData.jobSiteName && { jobSiteName: formData.jobSiteName.trim() }),
    ...(formData.zipCode && { zipCode: formData.zipCode.trim() }),
    ...(formData.notes && { notes: formData.notes.trim() }),
    ...(formData.specialInstructions && { specialInstructions: formData.specialInstructions.trim() }),
    ...(formData.jobPhone && { phoneNumber: formData.jobPhone.trim() })
  };
};

export const mapFormDataToUpdateInput = (formData: JobSiteFormData): UpdateJobSiteMutationInput => {
  if (
    formData.id == null ||
    formData.customerId == null ||
    !formData.address ||
    !formData.city ||
    !formData.state ||
    formData.latitude == null ||
    formData.longitude == null ||
    formData.powerlinePresent == null
  ) {
    throw new Error('Required fields are missing for updating a job site');
  }

  // Helper function to handle clearable string fields
  const mapClearableStringField = (value: string | null | undefined): string | null => {
    if (value === null || value === undefined || value.trim() === '') {
      return null;
    }
    return value.trim();
  };

  return {
    id: formData.id,
    customerId: formData.customerId,
    address: formData.address.trim(),
    city: formData.city.trim(),
    state: formData.state.trim(),
    latitude: Number(formData.latitude),
    longitude: Number(formData.longitude),
    powerlinePresent: formData.powerlinePresent,
    // Handle clearable ID field - send 0 when cleared
    jobSiteType: formData.jobSiteTypeId && formData.jobSiteTypeId !== 0 ? { id: formData.jobSiteTypeId } : { id: 0 },
    // Handle clearable ID field - send null when cleared
    ...(formData.salesAgent !== undefined && {
      salesAgent: formData.salesAgent != null && formData.salesAgent !== 0 ? { empId: Number(formData.salesAgent) } : null
    }),
    // Handle clearable string field - send null when cleared
    jobSiteName: formData.jobSiteName !== undefined ? mapClearableStringField(formData.jobSiteName) : null,
    // Handle clearable string field - send null when cleared
    ...(formData.zipCode !== undefined && { zipCode: mapClearableStringField(formData.zipCode) }),
    // Handle clearable string field - send null when cleared
    ...(formData.notes !== undefined && { notes: mapClearableStringField(formData.notes) }),
    // Handle clearable string field - send null when cleared
    ...(formData.specialInstructions !== undefined && { specialInstructions: mapClearableStringField(formData.specialInstructions) }),
    ...(formData.jobPhone !== undefined && { phoneNumber: mapClearableStringField(formData.jobPhone) })
  };
};

/**
 * Maps a V2 JobSite DTO to JobSiteFormData
 * Handles the differences between V2 schema and form data structure
 * Accepts partial DTO type since GraphQL queries may not fetch all fields
 */
export const mapJobSiteV2DTOToFormData = (
  jobSite?: Pick<
    Dtos_V2_JobSite_JobSiteDto,
    | 'jobsiteId'
    | 'jobName'
    | 'location'
    | 'cityTown'
    | 'state'
    | 'zip'
    | 'latitude'
    | 'longitude'
    | 'notes'
    | 'specialInstruction'
    | 'salesAgent'
    | 'jobsiteType'
    | 'flags'
    | 'customer'
    | 'availableJobSiteTypes'
    | 'deletable'
    | 'jobPhone'
  > | null
): JobSiteFormData => {
  if (!jobSite) {
    return {
      id: undefined,
      customerId: undefined,
      customerCompanyName: undefined,
      jobSiteName: '',
      jobSiteTypeId: undefined,
      powerlinePresent: false,
      jobPhone: '',
      address: '',
      city: '',
      state: '',
      country: '',
      zipCode: '',
      latitude: undefined,
      longitude: undefined,
      notes: '',
      specialInstructions: ''
    };
  }

  // Parse jobsiteId (string) to number for id
  const id = jobSite.jobsiteId && !isNaN(Number(jobSite.jobsiteId)) ? Number(jobSite.jobsiteId) : undefined;

  // Parse customer id (string) to number
  const customerId = jobSite.customer?.id && !isNaN(Number(jobSite.customer?.id)) ? Number(jobSite.customer?.id) : undefined;

  // Parse salesAgent (string) to number if it's numeric
  const salesAgent = jobSite.salesAgent && !isNaN(Number(jobSite.salesAgent)) ? Number(jobSite.salesAgent) : undefined;

  // Parse latitude/longitude (Decimal) to number
  const latitude = jobSite.latitude != null && !isNaN(Number(jobSite.latitude)) ? Number(jobSite.latitude) : undefined;
  const longitude = jobSite.longitude != null && !isNaN(Number(jobSite.longitude)) ? Number(jobSite.longitude) : undefined;

  // Get powerlinePresent from flags
  const powerlinePresent = jobSite.flags?.powerline ?? false;

  // Parse jobsiteType (string) to number if it's numeric, otherwise look up in availableJobSiteTypes
  let jobSiteTypeId: number | undefined = undefined;
  if (jobSite.jobsiteType) {
    const numericTypeId = Number(jobSite.jobsiteType);
    if (!isNaN(numericTypeId)) {
      jobSiteTypeId = numericTypeId;
    } else if (jobSite.availableJobSiteTypes) {
      // Try to find matching type by objectName
      const matchingType = jobSite.availableJobSiteTypes.find((type) => type.objectName === jobSite.jobsiteType);
      if (matchingType) {
        jobSiteTypeId = matchingType.id;
      }
    }
  }

  return {
    id,
    customerId,
    customerCompanyName: jobSite.customer?.name ?? undefined,
    jobSiteName: jobSite.jobName ?? '',
    jobSiteTypeId,
    powerlinePresent,
    jobPhone: jobSite.jobPhone ?? '',
    address: jobSite.location ?? '',
    city: jobSite.cityTown ?? '',
    state: jobSite.state ?? '',
    country: (jobSite as { country?: string | null }).country ?? '',
    zipCode: jobSite.zip ?? '',
    latitude,
    longitude,
    notes: jobSite.notes ?? '',
    specialInstructions: jobSite.specialInstruction ?? '',
    salesAgent
  };
};
